import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import ProductCard from '../components/ProductCard';
import { useProducts } from '../hooks/useProducts';
import { Archive, CheckCircle, Shield, Tag, Loader } from 'lucide-react';

const OpenBoxPage: React.FC = () => {
  const { products: filteredProducts, loading, error } = useProducts({ productType: 'open-box' });

  useEffect(() => {
    document.title = "Open Box Deals | Strum Music";
  }, []);

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading open box deals...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading open box deals</p>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-green-600 to-green-800 text-white py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <Archive className="h-16 w-16 mx-auto mb-4 text-green-200" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Open Box Deals
            </h1>
            <p className="text-xl text-green-100 max-w-3xl mx-auto">
              Get premium instruments at discounted prices. Open box items are in excellent condition 
              with full warranty and all original accessories.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Open Box Benefits */}
      <section className="py-12 bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Tag className="h-8 w-8 text-green-600" />,
                title: "Great Savings",
                description: "Save up to 20% off retail price on like-new instruments."
              },
              {
                icon: <CheckCircle className="h-8 w-8 text-green-600" />,
                title: "Excellent Condition",
                description: "Professionally inspected and tested to ensure perfect functionality."
              },
              {
                icon: <Shield className="h-8 w-8 text-green-600" />,
                title: "Full Warranty",
                description: "Complete manufacturer warranty and 30-day return guarantee."
              }
            ].map((benefit, index) => (
              <motion.div 
                key={index}
                className="text-center p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <div className="container-custom py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Open Box Deals</h2>
        
        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="relative"
              >
                <div className="absolute top-2 left-2 z-10">
                  <span className="bg-green-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
                    Open Box
                  </span>
                </div>
                <ProductCard product={product} />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Archive className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Open Box Items Available</h3>
            <p className="text-gray-600">Check back soon for new open box deals.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpenBoxPage;